/**
 * Möten – utskrift och sammanfattning av interna möten och kundmöten.
 *
 * `/moten` visar listan och "Nytt möte"; `/moten/:id` visar ett möte. Mötesvyn
 * får `key={id}` så att ett byte av möte aldrig bär med sig det förra mötets
 * state (en route med :id återanvänder annars komponenten).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Spinner } from '@/components/ui';
import { motenApi, type MotesListrad, type MotesListsvar } from '@/lib/api';
import { useAdmin } from '@/hooks/useAdmin';
import { MotesLista } from '@/components/features/moten/MotesLista';
import { MotesVy } from '@/components/features/moten/MotesVy';
import { NyttMote } from '@/components/features/moten/NyttMote';
import { RaderaMote } from '@/components/features/moten/RaderaMote';
import { felText } from '@/components/features/moten/motesText';
import { MotesGlyf } from '@/components/features/moten/MotesDelar';
import { MOTEN_SLAPPT } from '../../netlify/functions/_shared/moten/slapp';

export function MotenPage() {
  const { id } = useParams<{ id: string }>();
  const lage = useSlapplage();
  let innehall;
  if (lage === 'vantar') {
    innehall = (
      <div className="flex justify-center py-12">
        <Spinner size={22} className="text-brand-500" label="Hämtar möten" />
      </div>
    );
  } else if (lage === 'snart') innehall = <KommerSnart />;
  else innehall = id ? <MotesVy key={id} id={id} /> : <Oversikt />;
  return <div className="flex-1 overflow-y-auto p-4 sm:p-5 lg:p-6">{innehall}</div>;
}

/**
 * Före släppet (MOTEN_SLAPPT) avgör servern vem som ser Möten: admin enligt
 * klientens lista släpps in direkt, alla andra frågar /api/moten och får
 * kommer_snart. Inget visas förrän beskedet kommit, och ett fel ger det säkra
 * läget – så varken "Nytt möte" eller en direktlänk till /moten/:id lovar något.
 */
function useSlapplage(): 'oppet' | 'snart' | 'vantar' {
  const { isAdmin, loading } = useAdmin();
  const direkt = MOTEN_SLAPPT || isAdmin;
  const [besked, setBesked] = useState<'oppet' | 'snart' | null>(null);
  useEffect(() => {
    if (direkt || loading) return;
    let aktuell = true;
    motenApi<MotesListsvar>({ atgard: 'lista' })
      .then((r) => aktuell && setBesked(r.kommer_snart ? 'snart' : 'oppet'))
      .catch(() => aktuell && setBesked('snart'));
    return () => {
      aktuell = false;
    };
  }, [direkt, loading]);
  if (direkt) return 'oppet';
  return besked ?? 'vantar';
}

function Oversikt() {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [rader, setRader] = useState<MotesListrad[] | null>(null);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  const [nytt, setNytt] = useState(false);
  const [radera, setRadera] = useState<MotesListrad | null>(null);
  const [inspelar, setInspelar] = useState(false);
  const [tjanster, setTjanster] = useState<{ stt_pa?: boolean; klang_pa?: boolean }>({});
  const senaste = useRef(0);

  const hamta = useCallback(async () => {
    const nr = ++senaste.current;
    let svar: { rader: MotesListrad[]; stt_pa?: boolean; klang_pa?: boolean } | { fel: string };
    try {
      const r = await motenApi<MotesListsvar>({ atgard: 'lista' });
      svar = { rader: r.moten, stt_pa: r.stt_pa, klang_pa: r.klang_pa };
    } catch (e) {
      svar = { fel: felText(e, 'Att hämta möten') };
    }
    return { nr, svar };
  }, []);

  // Bara det senaste anropet får skriva; ett långsamt äldre svar kastas.
  const tillampa = useCallback(({ nr, svar }: Awaited<ReturnType<typeof hamta>>) => {
    if (nr !== senaste.current) return;
    if ('rader' in svar) {
      setRader(svar.rader);
      setTjanster({ stt_pa: svar.stt_pa, klang_pa: svar.klang_pa });
      setFel(null);
    } else setFel(svar.fel);
    setLaddar(false);
  }, []);

  const laddaOm = useCallback(() => void hamta().then(tillampa), [hamta, tillampa]);

  useEffect(() => {
    void hamta().then(tillampa);
  }, [hamta, tillampa]);

  const skapat = useCallback((nyttId: string) => navigate(`/moten/${nyttId}`, { state: { nytt: true } }), [navigate]);

  // Admin ser "Alla"; serverns relation 'admin' bekräftar det även om env-listan i klienten skulle sakna adressen.
  const arAdmin = isAdmin || (rader ?? []).some((r) => r.relation === 'admin');

  return (
    <div className="max-w-[1100px] mx-auto pb-10 space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-[13px] text-ink-600 max-w-[640px] leading-relaxed">
          Möten skrivs ut med talare och sammanfattas med beslut och åtgärder. Ljudet sparas i 30 dagar, texten tills
          någon raderar den.
        </p>
        {!nytt && (
          <Button onClick={() => setNytt(true)} className="self-start sm:self-auto shrink-0">
            Nytt möte
          </Button>
        )}
      </header>

      {nytt && (
        <NyttMote
          onSkapat={skapat}
          onAvbryt={() => setNytt(false)}
          onListaAndrad={laddaOm}
          onInspelarAndrad={setInspelar}
          sttPa={tjanster.stt_pa}
          klangPa={tjanster.klang_pa}
        />
      )}

      {/* Under inspelning är listan borta: en listrad är en länk som avmonterar inspelningen. */}
      {inspelar ? (
        <p className="px-1 text-[13px] text-ink-600">Möteslistan visas när inspelningen är avslutad.</p>
      ) : (
        <MotesLista rader={rader} laddar={laddar} fel={fel} arAdmin={arAdmin} onRadera={setRadera} />
      )}

      <RaderaMote
        mote={radera}
        onStang={() => setRadera(null)}
        onRaderat={() => {
          setRadera(null);
          laddaOm();
        }}
      />
    </div>
  );
}

/** Före släppet (MOTEN_SLAPPT): sidan lovar inget mer än att det kommer. */
function KommerSnart() {
  return (
    <section
      aria-labelledby="moten-snart-rubrik"
      className="max-w-[640px] mx-auto mt-6 rounded-xl border border-ink-200 bg-surface shadow-card px-6 py-8 text-center"
    >
      <MotesGlyf size={28} strokeWidth={1.75} className="mx-auto text-brand-500" />
      <h2 id="moten-snart-rubrik" className="mt-3 text-[16px] font-bold text-ink-900">
        Möten kommer inom kort
      </h2>
      <p className="mt-2 text-[13px] text-ink-600 leading-relaxed">
        Här kommer ni kunna få era möten utskrivna med talare och sammanfattade med beslut och åtgärder. Vi säger till
        när det är öppnat.
      </p>
    </section>
  );
}
